import { useCallback, useEffect, useState, useRef } from "react";

import useStore from "../../Store/Store";
import { openDirectory } from "../../utils/LocalFileController/LocalFileController";
import { logger } from "../../utils/Logger";
import "./AlternativeLandingComponent.css";
import { Typography, Tooltip } from "@mui/material";
import VideoTutorial from "../VideoTutorial/VideoTutorial";
import {
  AccountTree,
  DriveFolderUpload,
  GitHub,
  PlayArrow,
  School,
  Fullscreen,
  FullscreenExit,
  TrendingUp,
  FavoriteBorder,
} from "@mui/icons-material";
import { login } from "../../utils/AuthUtils";
import FirebaseAuth from "../Common/FirebaseAuth/FirebaseAuth";
import { loadPublicRepo } from "./LandingComponent";

// Import GIFs from MobileLandingPage
import customUsecaseGif from "../../Media/gifs/custom-usecase.gif";
import contextAndIoGif from "../../Media/gifs/context-and-io.gif";
import linkingGif from "../../Media/gifs/CodeLinkingCompressed.gif";
import docsGif from "../../Media/gifs/DocsCompressed.gif";
import scannerGif from "../../Media/gifs/CodeCanvasScannerCompressed.gif";
import safeGif from "../../Media/gifs/save.gif";
import chatGif from "../../Media/gifs/trimmed-chat-with-completion.gif";
import LandingSystemDesign from "../../Media/LandingSystemDesign";
import LandingLogo from "../../Media/LandingLogo";

// Preload all GIFs
const preloadImage = (src: string) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
};

const allGifs = [
  customUsecaseGif,
  contextAndIoGif,
  linkingGif,
  docsGif,
  scannerGif,
  safeGif,
  chatGif,
];

const SUPADEMO_URL =
  "https://app.supademo.com/embed/cmbz9nz9rhkgnsn1r1qp9tr09?embed_v=2";

declare module "react" {
  interface IframeHTMLAttributes<T> extends HTMLAttributes<T> {
    webkitAllowFullScreen?: string;
    mozAllowFullScreen?: string;
    allowFullScreen?: boolean;
  }
}

interface Feature {
  key: string;
  heading: string;
  subheading: string;
  visual: string;
  gifs: string[];
  gifCaptions: string[];
}

const features: Feature[] = [
  {
    key: "ai-diagrams",
    heading: "Diagrams & Context: Just Ask!",
    subheading:
      "Use AI to auto-generate diagrams and gain contextual understanding in seconds.",
    visual: "🤖🗺️",
    gifs: [customUsecaseGif, contextAndIoGif],
    gifCaptions: [
      "Just ask about a use case and get started understanding - no prior language experience needed!",
      "Get mocked details and simulation flow through the code responsible for the use case.",
    ],
  },
  {
    key: "docs-diagrams",
    heading: "Link Docs & Diagrams to Code",
    subheading:
      "Keep your documents and diagrams compact, and directly linked to code wherever you have your repository.",
    visual: "📄➔💻",
    gifs: [linkingGif, docsGif],
    gifCaptions: [
      "Make your diagrams plans first and link code as you work.",
      "Add team specific context to any code snippet.",
    ],
  },
  {
    key: "github-integration",
    heading: "Save Context with GitHub Integration",
    subheading:
      "Use our GitHub integration to save precious historical context and let engineers understand your changes years into the future.",
    visual: "🐙🕰️",
    gifs: [safeGif, scannerGif],
    gifCaptions: [
      "Push the diagram file directly to your branch or save it locally.",
      "Try enabling our Scanner which will remind you to update any diagram elements which have been affected in a PR!",
    ],
  },
  {
    key: "ai-chat",
    heading: "AI + Team Knowledge",
    subheading:
      "Combine your specialized team knowledge with the speed of AI to make the most informed decisions that actually fits your business constraints.",
    visual: "💬⚡",
    gifs: [chatGif],
    gifCaptions: [
      "Select paths in the code base to give exactly the context needed by LLMs to help guide your decisions.",
    ],
  },
];

const AlternativeLandingComponent = () => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const toggleFullscreen = () => {
    setIsTransitioning(true);
    // First fade out the button
    setTimeout(() => {
      setIsFullscreen(!isFullscreen);
      // Keep transitioning state until all animations complete
      setTimeout(() => {
        setIsTransitioning(false);
      }, 500); // Wait for slide + fade-in (300ms + 200ms)
    }, 150); // Initial button fade-out
  };

  const {
    isLoggedIn,
    directoryHandle,
    sessionMode,
    isUserChoosingDirectory,
    showLandingComponent,
    currentBranch,
    setShowLandingComponent,
    currentRepo,
    privateRepositoryPrompt,
    setPrivateRepositoryPrompt,
  } = useStore((state) => ({
    isLoggedIn: state.session.isLoggedIn,
    directoryHandle: state.session.local.directory.handle,
    sessionMode: state.session.mode,
    isUserChoosingDirectory: state.isUserChoosingDirectory,
    showLandingComponent: state.showLandingComponent,
    currentBranch: state.currentBranch,
    setShowLandingComponent: state.setShowLandingComponent,
    currentRepo: state.currentRepo,
    privateRepositoryPrompt: state.privateRepositoryPrompt,
    setPrivateRepositoryPrompt: state.setPrivateRepositoryPrompt,
  }));

  const handleLogin = useCallback(async () => {
    if (!isLoggedIn) {
      if (privateRepositoryPrompt) {
        setPrivateRepositoryPrompt(false);
      }
      login();
      return;
    }
  }, [isLoggedIn, privateRepositoryPrompt, setPrivateRepositoryPrompt]);

  const loadDemo = () => {
    const currentDomain = window.location.origin;
    const demoURL = `${currentDomain}/?session=unauthenticatedGithub&repo=react-ecommerce&owner=Abdulnaser97&branch=master`;
    setShowLandingComponent(false);
    window.location.assign(demoURL);
  };

  const goNotion = () => {
    window.location.assign(
      `https://pie-crepe-38f.notion.site/Documentation-cf20cbdf134247cb93bf6366d9055076`
    );
  };

  const handleOpenFolder = async () => {
    try {
      await openDirectory(true);
      setShowLandingComponent(false);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (privateRepositoryPrompt) {
      setShowLandingComponent(true);
      return;
    }

    if (
      (sessionMode === "unauthenticatedGithub" && !currentRepo) ||
      (sessionMode === "local" && !directoryHandle && !isUserChoosingDirectory)
    ) {
      setShowLandingComponent(true);
    } else {
      setShowLandingComponent(false);
      if (privateRepositoryPrompt) {
        setPrivateRepositoryPrompt(false);
      }
    }
  }, [
    directoryHandle,
    sessionMode,
    isUserChoosingDirectory,
    setShowLandingComponent,
    currentRepo,
    privateRepositoryPrompt,
    setPrivateRepositoryPrompt,
  ]);

  const [publicURL, setPublicURL] = useState("");
  const [imagesLoaded, setImagesLoaded] = useState(false);

  // Preload all GIFs when component mounts
  useEffect(() => {
    const loadAllImages = async () => {
      try {
        await Promise.all(allGifs.map((gif) => preloadImage(gif)));
        setImagesLoaded(true);
      } catch (error) {
        console.error("Failed to preload images:", error);
        // Still set as loaded to show images even if preload fails
        setImagesLoaded(true);
      }
    };
    loadAllImages();
  }, []);
  const handlePublicURLChange = (e) => {
    setPublicURL(e.target.value);
  };

  const handleEnterKey = (e) => {
    if (e.key === "Enter") {
      handleLoadRepo();
    }
  };

  const handleLoadRepo = () => {
    if (publicURL.trim()) {
      if (privateRepositoryPrompt) {
        setPrivateRepositoryPrompt(false);
      }

      try {
        // Parse the GitHub URL to extract owner and repo
        const githubUrl = publicURL.trim();
        let url: URL;

        try {
          url = new URL(githubUrl);
        } catch (error) {
          throw new Error("Invalid URL format");
        }

        // Check if it's a GitHub URL
        if (!url.hostname.includes("github.com")) {
          throw new Error("Please enter a valid GitHub repository URL");
        }

        // Extract owner and repo from path
        const pathParts = url.pathname.split("/").filter(Boolean);
        if (pathParts.length < 2) {
          throw new Error("Invalid GitHub repository URL format");
        }

        const owner = pathParts[0];
        const repo = pathParts[1];

        // Get current domain and construct CodeCanvas-style URL
        const currentDomain = window.location.origin;
        const codeCanvasUrl = `${currentDomain}/${owner}/${repo}`;

        logger.debug.info(
          `Redirecting from GitHub URL ${githubUrl} to CodeCanvas URL ${codeCanvasUrl}`
        );

        // Clear the input field and hide landing component
        setPublicURL("");
        setShowLandingComponent(false);

        // Redirect to CodeCanvas-style URL (this will trigger the same authentication flow as direct URL navigation)
        window.location.assign(codeCanvasUrl);
      } catch (error) {
        logger.error("Error processing GitHub URL:", error);
        useStore
          .getState()
          .setErrorNotification(
            error.message ||
              "Failed to process repository URL. Please check the URL format and try again."
          );
      }
    }
  };

  return (
    <>
      {showLandingComponent && (
        <div className="alt-landing-wrapper">
          <div className="alt-landing-auth">
            <FirebaseAuth />
          </div>

          <div
            className={`alt-landing-split ${isFullscreen ? "fullscreen" : ""} ${
              isTransitioning ? "transitioning" : ""
            }`}
          >
            <div className="alt-landing-left">
              <div className="alt-landing-content">
                <div className="alt-landing-title">
                  <h1 style={{ marginBottom: "10vh" }}>
                    <div className="landing-logo-wrapper">
                      <LandingLogo />
                    </div>
                    CodeCanvas
                  </h1>
                </div>
                <div className="alt-landing-subtitle">
                  LLM + Interactive Diagrams + Mapped To Source Code ={" "}
                  <FavoriteBorder
                    style={{
                      color: "#FF7B7B",
                      fontSize: "1.2em",
                      verticalAlign: "middle",
                      marginLeft: "4px",
                    }}
                  />
                </div>

                <div className="alt-landing-description">
                  Generate code-level diagrams and onboard to any codebase with{" "}
                  <br />
                  <span
                    style={{
                      color: "#ffffff",
                      fontWeight: "700",
                      backgroundColor: "#FF7B7B",
                      padding: "4px 8px",
                      borderRadius: "6px",
                      boxShadow: "0 2px 4px rgba(255, 123, 123, 0.2)",
                      border: "1px solid #FF9B9B",
                    }}
                  >
                    one click
                  </span>
                  .
                </div>

                {privateRepositoryPrompt && (
                  <div className="alt-landing-private-repo-alert">
                    <Typography variant="h6">
                      🔒 Private Repository Detected
                    </Typography>
                    <Typography>
                      This repository appears to be private. Please sign in with
                      GitHub to access it.
                    </Typography>
                  </div>
                )}

                <div className="alt-landing-actions">
                  <div className="alt-landing-url-input-wrapper">
                    <input
                      placeholder="Enter GitHub Repo URL"
                      className="alt-landing-url-input"
                      onChange={handlePublicURLChange}
                      onKeyUp={handleEnterKey}
                      value={publicURL}
                      type="url"
                    />

                    <div className="alt-landing-url-caption">
                      <div className="replace-text">
                        Replace "<span className="strike-text">github</span>"
                        with "
                        <span className="highlight-text">code-canvas</span>" to
                        explore any public repository
                      </div>
                      <div className="example">
                        e.g. https://<span className="strike-text">github</span>
                        .com/cli/cli <span className="arrow">→</span> https://
                        <span className="highlight-text">code-canvas</span>
                        .com/cli/cli
                      </div>
                    </div>
                  </div>

                  <div className="alt-landing-button-sections">
                    <div className="alt-landing-button-column">
                      <div className="alt-landing-column-title">
                        Private Repo?
                      </div>
                      <button
                        className="alt-landing-button"
                        onClick={handleOpenFolder}
                      >
                        <div className="alt-landing-button-icon-wrapper">
                          <DriveFolderUpload />
                        </div>
                        <Typography variant="caption" fontWeight="bold">
                          Open From Local
                        </Typography>
                      </button>

                      <button
                        className={`alt-landing-button ${
                          privateRepositoryPrompt
                            ? "private-repo-highlight"
                            : ""
                        }`}
                        onClick={handleLogin}
                      >
                        <div className="alt-landing-button-icon-wrapper github-icon">
                          <GitHub />
                        </div>
                        <Typography variant="caption" fontWeight="bold">
                          Login with GitHub
                        </Typography>
                      </button>
                    </div>

                    <div className="alt-landing-button-column">
                      <div className="alt-landing-column-title">
                        Try it live!
                      </div>

                      <button className="alt-landing-button" onClick={loadDemo}>
                        <div className="alt-landing-button-icon-wrapper">
                          <AccountTree />
                        </div>
                        <Typography variant="caption" fontWeight="bold">
                          Demo
                        </Typography>
                      </button>

                      <button
                        className="alt-landing-button"
                        onClick={() =>
                          window.open("https://github.com/trending", "_blank")
                        }
                      >
                        <Tooltip
                          title="Try replacing 'github' in the URL of any trending repository to explore it in CodeCanvas!"
                          placement="top"
                        >
                          <div
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <div className="alt-landing-button-icon-wrapper">
                              <TrendingUp />
                            </div>
                            <Typography variant="caption" fontWeight="bold">
                              Trending Repos
                            </Typography>
                          </div>
                        </Tooltip>
                      </button>
                    </div>

                    <div className="alt-landing-button-column">
                      <div className="alt-landing-column-title">Learn More</div>

                      <button className="alt-landing-button" onClick={goNotion}>
                        <div className="alt-landing-button-icon-wrapper">
                          <School />
                        </div>
                        <Typography variant="caption" fontWeight="bold">
                          Documentation
                        </Typography>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="alt-landing-footer">
                  <Typography variant="caption">
                    By using Code Canvas, you agree to our{" "}
                    <a
                      href="https://pie-crepe-38f.notion.site/CodeCanvas-Terms-and-Conditions-ef6b3e7a3ee54da3b363c48112062bce?pvs=4"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      terms of service
                    </a>{" "}
                    and{" "}
                    <a
                      href="https://pie-crepe-38f.notion.site/CodeCanvas-Privacy-Policy-5466713f74724c9ab0da9a1d669ab379?pvs=4"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      privacy policy
                    </a>
                  </Typography>
                </div>
              </div>
            </div>

            <div className="alt-landing-right">
              <button
                className="alt-landing-fullscreen-toggle"
                onClick={toggleFullscreen}
                aria-label={
                  isFullscreen ? "Get Started In The App" : "Enter full screen"
                }
              >
                {isFullscreen ? (
                  <>
                    <FullscreenExit />
                    <span>Get Started In The App</span>
                  </>
                ) : (
                  <>
                    <Fullscreen />
                    <span>Full Screen</span>
                  </>
                )}
              </button>

              <div className="alt-landing-svg-placeholder">
                <LandingSystemDesign />
              </div>
              <div className="learn-more-indicator">
                <Typography
                  variant="h5"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    justifyContent: "center",
                    animation: "bounce 2s infinite",
                  }}
                >
                  Learn More
                </Typography>
                <span style={{ fontSize: "30px" }}>↓</span>
              </div>
              <div className="alt-landing-supademo-section">
                <Typography
                  variant="h3"
                  style={{
                    color: "#333",
                    fontWeight: 600,
                    marginBottom: "24px",
                    textAlign: "center",
                    fontFamily: "Poppins-Medium, sans-serif",
                  }}
                >
                  View Our Interactive Demo
                </Typography>
                <Typography
                  variant="h6"
                  style={{
                    color: "#666",
                    marginBottom: "32px",
                    textAlign: "center",
                    maxWidth: "600px",
                    margin: "0 auto 32px",
                  }}
                >
                  This is just one natural way we've used our app to understand
                  new code.
                </Typography>
                {/* <div className="alt-landing-supademo-container"> */}
                <iframe
                  src="https://app.supademo.com/embed/cmbz9nz9rhkgnsn1r1qp9tr09?embed_v=2"
                  frameBorder="0"
                  allowFullScreen={true}
                  style={{
                    width: "100%",
                    height: "100%",
                    minHeight: isFullscreen ? "75vh" : "45vh",
                    // height:'100%',
                    borderRadius: "12px",
                    //   boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                    padding: "0px",
                    margin: "0px",
                    border: "0px",
                    overflow: "hidden",
                    backgroundColor: "transparent",
                  }}
                />
              </div>

              <div className="alt-landing-features">
                {/* <div className="alt-landing-feature-item"> */}

                {features.map((feature, index) => (
                  <div key={feature.key} className="alt-landing-feature-item">
                    <Typography
                      variant="h3"
                      style={{
                        color: "#333",
                        fontWeight: 500,
                        marginBottom: "10px",
                        textAlign: "center",
                        fontFamily: "Poppins, sans-serif",
                      }}
                    >
                      {feature.heading}
                    </Typography>
                    <Typography
                      variant="h6"
                      style={{
                        color: "#666",
                        marginBottom: "48px",
                        textAlign: "center",
                      }}
                    >
                      {feature.subheading}
                    </Typography>

                    <div className="alt-landing-feature-visual">
                      {imagesLoaded &&
                        feature.gifs.map((gif, gifIdx) => (
                          <div
                            key={`${feature.key}-gif-${gifIdx}`}
                            className="gif-container"
                          >
                            <img
                              src={gif}
                              alt={`${feature.heading} example ${gifIdx + 1}`}
                              style={{
                                width: "100%",
                                height: "auto",
                                borderRadius: "12px",
                                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
                                margin: "0 auto",
                                display: "block",
                              }}
                            />
                            {feature.gifCaptions[gifIdx] && (
                              <Typography
                                variant="body2"
                                style={{
                                  color: "#666",
                                  fontStyle: "italic",
                                  textAlign: "center",
                                  marginTop: "16px",
                                  marginBottom: "32px",
                                }}
                              >
                                {feature.gifCaptions[gifIdx]}
                              </Typography>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      <VideoTutorial />
    </>
  );
};

export default AlternativeLandingComponent;
